/**
 * Facebook Pixel tracking utilities
 * Provides helper functions for tracking various events with Facebook Pixel
 */

/**
 * Track a custom event with Facebook Pixel
 * @param {string} eventName - The event name to track
 * @param {object} parameters - Event parameters
 * @param {string} [eventId] - Meta's eventID, the fourth fbq argument. The
 *   same event name and eventID sent to the pixel within 48 hours is kept
 *   once, and it is what a Conversions API event_id would match.
 */
export const trackFacebookPixelEvent = (
  eventName,
  parameters = {},
  eventId = null
) => {
  if (typeof window !== "undefined" && window.fbq) {
    if (eventId) {
      window.fbq("track", eventName, parameters, { eventID: String(eventId) });
    } else {
      window.fbq("track", eventName, parameters);
    }
  }
};

/**
 * Track webinar registration event
 * @param {object} webinarData - Webinar information
 * @param {object} userData - User registration data
 */
export const trackWebinarRegistration = (webinarData, userData = {}) => {
  trackFacebookPixelEvent("CompleteRegistration", {
    content_name: webinarData?.title,
    content_category: webinarData?.category,
    content_ids: [webinarData?.id],
    value: webinarData?.price || 0,
    currency: "IDR",
    // Custom parameters
    webinar_date: webinarData?.date,
    user_organization: userData?.organization,
    user_industry: userData?.industry,
    user_job_title: userData?.jobTitle,
  });
};

/**
 * Track webinar view event
 * @param {object} webinarData - Webinar information
 */
export const trackWebinarView = (webinarData) => {
  trackFacebookPixelEvent("ViewContent", {
    content_type: "webinar",
    content_name: webinarData?.title,
    content_category: webinarData?.category,
    content_ids: [webinarData?.id],
    value: webinarData?.price || 0,
    currency: "IDR",
  });
};

/**
 * Track initiate checkout events
 * @param {object} checkoutData - Checkout information
 * @param {Array} checkoutData.products - Array of products being checked out
 */
export const trackInitiateCheckout = (checkoutData) => {
  const products = checkoutData?.products || [];

  // Calculate total value and quantity
  const totalValue = products.reduce((sum, product) => {
    return sum + product.price * (product.quantity || 1);
  }, 0);

  const totalQuantity = products.reduce((sum, product) => {
    return sum + (product.quantity || 1);
  }, 0);

  // Build content_ids array
  const contentIds = products.map((product) => product.id);

  // Build contents array with id and quantity
  const contents = products.map((product) => ({
    id: product.id,
    quantity: product.quantity || 1,
  }));

  trackFacebookPixelEvent("InitiateCheckout", {
    content_ids: contentIds,
    contents: contents,
    currency: "IDR",
    num_items: totalQuantity,
    value: totalValue,
  });
};

/**
 * Track purchase events (actual completed purchases)
 * @param {object} purchaseData - Purchase information
 * @param {Array} purchaseData.products - Array of products purchased
 * @param {string} [purchaseData.transactionId] - Sent as the eventID, so a
 *   repeat (another device, cleared storage) is dropped by Meta
 * @param {number} [purchaseData.value] - Amount actually paid (totalPay), the
 *   same value GA4 gets; falls back to the product total
 */
export const trackPurchase = (purchaseData) => {
  const products = purchaseData?.products || [];

  // Calculate total value and quantity
  const totalValue = products.reduce((sum, product) => {
    return sum + product.price * (product.quantity || 1);
  }, 0);

  const totalQuantity = products.reduce((sum, product) => {
    return sum + (product.quantity || 1);
  }, 0);

  // Build content_ids array
  const contentIds = products.map((product) => product.id);

  // Build contents array with id and quantity
  const contents = products.map((product) => ({
    id: product.id,
    quantity: product.quantity || 1,
  }));

  trackFacebookPixelEvent(
    "Purchase",
    {
      content_type: "product",
      content_ids: contentIds,
      contents: contents,
      currency: "IDR",
      num_items: totalQuantity,
      value: purchaseData?.value ?? totalValue,
    },
    purchaseData?.transactionId
  );
};

/**
 * Track add to cart events
 * @param {object} cartData - Cart information
 * @param {Array} cartData.products - Array of products being added to cart
 */
export const trackAddToCart = (cartData) => {
  const products = cartData?.products || [];

  // Calculate total value
  const totalValue = products.reduce((sum, product) => {
    return sum + product.price * (product.quantity || 1);
  }, 0);

  // Build content_ids array
  const contentIds = products.map((product) => product.id);

  // Build contents array with id and quantity
  const contents = products.map((product) => ({
    id: product.id,
    quantity: product.quantity || 1,
  }));

  trackFacebookPixelEvent("AddToCart", {
    content_type: "product",
    content_ids: contentIds,
    contents: contents,
    currency: "IDR",
    value: totalValue,
  });
};

/**
 * Track lead generation events
 * @param {object} leadData - Lead information
 */
export const trackLead = (leadData = {}) => {
  trackFacebookPixelEvent("Lead", {
    content_name: leadData?.source,
    content_category: leadData?.category,
    value: leadData?.value || 0,
    currency: "IDR",
  });
};

/**
 * Track custom conversion events
 * @param {string} eventName - Custom event name
 * @param {object} eventData - Event data
 */
export const trackCustomConversion = (eventName, eventData = {}) => {
  trackFacebookPixelEvent(eventName, eventData);
};
